import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { useQueryClient } from "@tanstack/react-query";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import {
  Currency,
  DepositRefundedAt,
  HoldingDepositRefundedBy,
} from "@/gql/graphql";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useTranslations } from "use-intl";
import { useFormikContext } from "formik";
import { HoldingDepositRefundedByInput } from "@/app/Common/Components/HoldingDepositRefundedByInput";
import { DepositRefundedAtInput } from "@/app/Common/Components/DepositRefundedAtInput";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { relocationFeesValidation } from "@/app/Relocations/Validation/relocationValidation";

type FeesFormValues = {
  booking_fee_amount: number;
  charge_per_move_amount: number | null;
  holding_deposit_amount: number;
  holding_deposit_refunded_by: HoldingDepositRefundedBy | null;
  holding_deposit_refunded_at: DepositRefundedAt | null;
  currency: Currency;
};

function FeesFormFields() {
  const tForm = useTranslations("relocation.form");
  const { values, setFieldValue } = useFormikContext<FeesFormValues>();

  return (
    <div className="col-span-full grid grid-cols-3 gap-4 sm:grid-cols-6">
      <CurrencyInput
        currency={values.currency}
        name="booking_fee_amount"
        label={tForm("bookingFee")}
        className="col-span-2"
      />
      <CurrencyInput
        label={tForm("chargePerMove")}
        name="charge_per_move_amount"
        currency={values.currency}
        className="col-span-2"
      />
      <CurrencyInput
        currency={values.currency}
        name="holding_deposit_amount"
        label={tForm("holdingDeposit")}
        className="col-span-2"
      />
      {values.holding_deposit_amount > 0 ? (
        <>
          <HoldingDepositRefundedByInput
            name="holding_deposit_refunded_by"
            label={tForm("refundedBy")}
            onChange={(value) => {
              if (value === HoldingDepositRefundedBy.Imoova) {
                setFieldValue(
                  "holding_deposit_refunded_at",
                  DepositRefundedAt.Delivery,
                );
              }
            }}
            className="col-span-3"
          />
          <DepositRefundedAtInput
            isEditable={
              values.holding_deposit_refunded_by !==
              HoldingDepositRefundedBy.Imoova
            }
            name="holding_deposit_refunded_at"
            label={tForm("refundedAt")}
            className="col-span-3"
          />
        </>
      ) : null}
    </div>
  );
}

function FeesViewState({ record }: { record: FeesFormValues }) {
  const tForm = useTranslations("relocation.form");

  return (
    <dl className="col-span-full grid grid-cols-3 gap-4 text-sm">
      <div>
        <dt className="text-gray-500">{tForm("bookingFee")}</dt>
        <dd className="font-medium">
          {formatCurrency(record.booking_fee_amount, record.currency)}
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("chargePerMove")}</dt>
        <dd className="font-medium">
          {formatCurrency(record.charge_per_move_amount ?? 0, record.currency)}
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("holdingDeposit")}</dt>
        <dd className="font-medium">
          {formatCurrency(record.holding_deposit_amount, record.currency)}
        </dd>
      </div>
      {record.holding_deposit_amount > 0 ? (
        <>
          <div>
            <dt className="text-gray-500">{tForm("refundedBy")}</dt>
            <dd className="font-medium capitalize">
              {record.holding_deposit_refunded_by?.toLowerCase() ?? "--"}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{tForm("refundedAt")}</dt>
            <dd className="font-medium capitalize">
              {record.holding_deposit_refunded_at?.toLowerCase() ?? "--"}
            </dd>
          </div>
        </>
      ) : null}
    </dl>
  );
}

export function FeesFormCard() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useGqlMutation(updateRelocationMutation, {
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const record = useRelocationRecord();

  const initialValues: FeesFormValues = {
    booking_fee_amount: record.booking_fee_amount,
    charge_per_move_amount: record.charge_per_move_amount,
    holding_deposit_amount: record.holding_deposit_amount,
    holding_deposit_refunded_by: record.holding_deposit_refunded_by,
    holding_deposit_refunded_at: record.holding_deposit_refunded_at,
    currency: record.currency,
  };

  return (
    <RecordScreenFormCard<FeesFormValues>
      adminOnly
      containerClassName="rounded-lg border border-gray-200 bg-white p-6"
      title="Fees"
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(relocationFeesValidation)}
      view={<FeesViewState record={initialValues} />}
      onSubmit={async (values) => {
        await mutateAsync({
          id: record.id,
          input: {
            booking_fee_amount: values.booking_fee_amount,
            charge_per_move_amount: values.charge_per_move_amount,
            holding_deposit_amount: values.holding_deposit_amount,
            holding_deposit_refunded_by: values.holding_deposit_refunded_by,
            holding_deposit_refunded_at: values.holding_deposit_refunded_at,
          },
        });
      }}
    >
      <FeesFormFields />
    </RecordScreenFormCard>
  );
}
